import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { CompositeModel } from '../complex/composite.model';
import { NumberModelOptions } from '../options/number-model-options';
import { NumberModel } from '../primitive/number.model';
import { TextModel } from '../primitive/text.model';
import { ConfigConstants } from '../../config/config-constants';

export class MaterialCheckModel extends CompositeModel {
    public materialNr: TextModel;
    public quantity: NumberModel;
    public storageLoc: TextModel;

    public descTxt: string;
    public customerPrice: number;
    public customerCurrency: string;
    public repairPrice: number;
    public repairCurrency: string;
    public available: number;
    public extUnit: string;
    public storageLocDesc: string;
    public ndfQuote = 0;
    public ndfCounter = 0;
    public tsPercentage = 0;
    public tsPercentageCounter = 0;
    public longText: string[] = [];
    public deliveryCutoffTimesLText: ODataMatLTextModel[] = [];

    public constructor(materialNo: string, quantity: number, storageLoc: string) {
        super('MaterialCheckModel');
        this.materialNr = <TextModel>this.set(new TextModel('Material', materialNo)); // initial value: no content -> to be entered by user
        this.quantity = <NumberModel>this.set(
            new NumberModel(
                'Quantity',
                quantity,
                new NumberModelOptions({
                    minValue: 1,
                    required: true,
                }),
            ),
        );
        this.storageLoc = <TextModel>this.set(new TextModel('StorageLoc', storageLoc));
    }

    public getOutModel(): ODataMaterialCheckOutModel {
        const outModel = <ODataMaterialCheckOutModel>super.getOutModel();
        // modify data type of 'quantity' as gateway expects a string, not a number
        outModel.Quantity = outModel.Quantity.toString();
        return outModel;
    }

    /**
     * This function needs to be called once the result of the availability check arrives from the gateway
     *
     * @param data
     * @param longTextResponse
     */
    public updateFromResponse(data: ODataMaterialCheckModel, longTextResponse: ODataMatLTextModel[] = []): void {
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        this.descTxt = data.DescTxt;
        this.customerPrice = parseFloat(data.CustomerPrice);
        this.customerCurrency = data.CustomerCurrency;
        this.repairPrice = parseFloat(data.RepairPrice);
        this.repairCurrency = data.RepairCurrency;
        this.available = parseInt(data.Available, 10);
        this.extUnit = data.ExtUnit;
        this.quantity.options = {
            ...this.quantity.options,
            unit: this.extUnit,
        };
        this.storageLoc.value = data.StorageLoc;
        this.storageLocDesc = data.StorageLocDesc;
        this.ndfQuote = parseInt(data.NDFQuote, 10);
        this.ndfCounter = parseInt(data.NDFCounter, 10);
        this.tsPercentage = parseInt(data.TSPercentage, 10);
        this.tsPercentageCounter = parseInt(data.TSPercentageCounter, 10);
        if (longTextResponse.length > 0) {
            const getLTextLinesToBeDisplayed = configService.getMatLTextDisplayLines();
            const materialLText: ODataMatLTextModel[] = [];

            for (const lText of longTextResponse) {
                if (lText.Material !== data.Material) continue;
                if (lText.Tdid !== ConfigConstants.SLOC) {
                    materialLText.push(lText);
                } else {
                    this.deliveryCutoffTimesLText.push(lText);
                }
            }

            getLTextLinesToBeDisplayed.forEach((lineNumber) => {
                const index = parseInt(lineNumber, 10);
                if (materialLText[index] && materialLText[index].TextLine.length > 0) {
                    this.longText.push(materialLText[index].TextLine);
                }
            });
        }
    }

    /**
     * Method to retrieve TextLines where Tdid is SLOC
     * extracts the values (comma separated values) each of which
     * correspond to Delivery Cut off times for CSML Code, Name, Quantity, State, Overnight, Saturday and Heavyweight
     * It accounts for empty spaces and print N/A in its place
     * Also accounts for completely empty TextLine and will skip those
     *
     * @example FCLE,QUANTITY:1,DEP. BEREA(DHL SC), OH,16:00 EST,11:00 EST,15:00 EST
     * @returns DeliveryCutoffTimes array
     */
    public getDeliveryCutoffTimes(): DeliveryCutoffTimes[] {
        const deliveryCutoffTimesLines: DeliveryCutoffTimes[] = [];
        const textLines = this.deliveryCutoffTimesLText;

        textLines.forEach((line) => {
            if (!line.TextLine?.trim()) {
                return;
            }

            const [csmlCode, quantityWithLabel, csmlName, state, overnight, saturday, heavyweight] =
                line.TextLine.split(',');
            const quantity = quantityWithLabel?.split(':')[1].trim() || ConfigConstants.DeliveryCutOffTimeNotAvailable;

            deliveryCutoffTimesLines.push({ csmlCode, quantity, csmlName, state, overnight, saturday, heavyweight });
        });

        return deliveryCutoffTimesLines;
    }
}

export interface ODataMaterialCheckModel {
    Material: string;
    Quantity: string;
    DescTxt: string;
    CustomerPrice: string;
    CustomerCurrency: string;
    RepairPrice: string;
    RepairCurrency: string;
    Available: string;
    ExtUnit: string;
    StorageLoc: string;
    StorageLocDesc: string;
    NDFQuote: string;
    NDFCounter: string;
    TSPercentage: string;
    TSPercentageCounter: string;
}

export interface ODataMatLTextModel {
    Material: string;
    Tdid: string;
    TextLine: string;
}

export interface ODataMaterialAvailabilityModel {
    FrontId: string;
    LtextSet: Array<{ Material: string }>;
    PartSet: {
        results: ODataMaterialCheckModel[];
    };
}

export interface ODataMaterialAvailabilityResponseModel {
    FrontId: string;
    LtextSet: { results: ODataMatLTextModel[] };
    PartSet: {
        results: ODataMaterialCheckModel[];
    };
    __metadata: {
        uri: string;
        id: string;
        type: 'HC_SM_NGM_58_SRV.MatAvailability';
    };
}

export interface ODataMaterialPricingModel {
    FrontId: string;
    LtextSet: Array<{ Material: string }>;
    NotificationNo: string;
    PartSet: {
        results: ODataMaterialCheckModel[];
    };
}

export interface ODataMaterialPricingResponseModel {
    FrontId: string;
    LtextSet: { results: ODataMatLTextModel[] };
    NotificationNo: string;
    PartSet: {
        results: ODataMaterialCheckModel[];
    };
    __metadata: {
        uri: string;
        id: string;
        type: 'HC_SM_NGM_58_SRV.MatAvailability';
    };
}

export interface ODataMaterialCheckOutModel extends ODataObject {
    Material: string;
    Quantity: string;
    StorageLoc: string;
}

export interface DeliveryCutoffTimes {
    csmlCode: string;
    quantity: string;
    csmlName: string;
    state: string;
    overnight: string;
    saturday: string;
    heavyweight: string;
}
